import { brandColor } from '../../../_helpers/brand-color';
import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Subscription, skip } from 'rxjs';
import { ChartData, ChartOptions } from 'chart.js';

import { BaseComponent } from '../../../components/base/base.component';
import { SessionService } from '../../../_services/session/session.service';
import {
  StatsFilterService,
  ComparisonPeriod,
} from '../statistics-filter.service';
import {
  StatsDataService,
  TimeSeriesPoint,
  RevenueByTreatment,
  CategoryData,
  RevenueSummary,
} from '../statistics-data.service';

// Components
import { KpiCardComponent } from '../_components/kpi-card/kpi-card.component';
import { StatChartComponent } from '../_components/stat-chart/stat-chart.component';

// PrimeNG
import { TableModule } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { TooltipModule } from 'primeng/tooltip';

@Component({
  standalone: true,
  selector: 'app-stats-revenue',
  templateUrl: './revenue.component.html',
  styleUrls: ['./revenue.component.scss'],
  imports: [
    CommonModule,
    TranslateModule,
    KpiCardComponent,
    StatChartComponent,
    TableModule,
    ButtonModule,
    TooltipModule,
  ],
})
export class StatsRevenueComponent
  extends BaseComponent
  implements OnInit, OnDestroy
{
  private subscriptions: Subscription[] = [];

  // Loading states
  public loadingKpis: boolean = true;
  public loadingTimeline: boolean = true;
  public loadingByTreatment: boolean = true;
  public loadingByCategory: boolean = true;
  public loadingByPayment: boolean = true;

  // Data
  public revenueSummary: RevenueSummary | null = null;
  public revenueTimeline: TimeSeriesPoint[] = [];
  public revenueByTreatment: RevenueByTreatment[] = [];
  public revenueByCategory: CategoryData[] = [];
  public revenueByPayment: CategoryData[] = [];

  // Comparison period info
  public comparisonPeriod: ComparisonPeriod | null = null;

  // Charts
  public timelineChartData: ChartData | null = null;
  public timelineChartOptions: ChartOptions = {};
  public categoryChartData: ChartData | null = null;
  public categoryChartOptions: ChartOptions = {};
  public paymentChartData: ChartData | null = null;
  public paymentChartOptions: ChartOptions = {};

  constructor(
    public session: SessionService,
    private filterService: StatsFilterService,
    private dataService: StatsDataService,
    private translate: TranslateService,
  ) {
    super(session);
  }

  ngOnInit(): void {
    super.ngOnInit();
    this.loadData();

    this.subscriptions.push(
      this.filterService.filters$.pipe(skip(1)).subscribe(() => {
        this.loadData();
      }),
    );

    this.subscriptions.push(
      this.translate.onLangChange.subscribe(() => {
        if (this.revenueTimeline.length) this.buildTimelineChart();
        if (this.revenueByCategory.length) this.buildCategoryChart();
        if (this.revenueByPayment.length) this.buildPaymentChart();
      }),
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }

  loadData(): void {
    const customerId = this.session.getCustomer()?.id;
    const siteId = this.session.getSite()?.id;

    if (!customerId || !siteId) return;

    // Update comparison period info
    this.comparisonPeriod = this.filterService.comparisonPeriod;

    // Revenue Summary KPIs
    this.loadingKpis = true;
    this.dataService.getRevenueSummary(customerId, siteId).subscribe({
      next: (data) => {
        this.revenueSummary = data;
        this.loadingKpis = false;
      },
      error: () => (this.loadingKpis = false),
    });

    // Revenue Timeline
    this.loadingTimeline = true;
    const granularity = this.filterService.currentFilters.granularity;
    this.dataService
      .getRevenueTimeline(customerId, siteId, granularity)
      .subscribe({
        next: (data) => {
          this.revenueTimeline = data;
          this.buildTimelineChart();
          this.loadingTimeline = false;
        },
        error: () => (this.loadingTimeline = false),
      });

    // Revenue by Treatment
    this.loadingByTreatment = true;
    this.dataService.getRevenueByTreatment(customerId, siteId).subscribe({
      next: (data) => {
        this.revenueByTreatment = data;
        this.loadingByTreatment = false;
      },
      error: () => (this.loadingByTreatment = false),
    });

    // Revenue by Category
    this.loadingByCategory = true;
    this.dataService.getRevenueByCategory(customerId, siteId).subscribe({
      next: (data) => {
        this.revenueByCategory = data;
        this.buildCategoryChart();
        this.loadingByCategory = false;
      },
      error: () => (this.loadingByCategory = false),
    });

    // Revenue by Payment
    this.loadingByPayment = true;
    this.dataService
      .getPaymentMethodDistribution(customerId, siteId)
      .subscribe({
        next: (data) => {
          this.revenueByPayment = data;
          this.buildPaymentChart();
          this.loadingByPayment = false;
        },
        error: () => (this.loadingByPayment = false),
      });
  }

  private buildTimelineChart(): void {
    const labels = this.revenueTimeline.map((p) => p.label || p.date);
    const values = this.revenueTimeline.map((p) => p.value);

    this.timelineChartData = {
      labels,
      datasets: [
        {
          label: this.translate.instant('STATISTICS.REVENUE.CHARTS.REVENUE'),
          data: values,
          backgroundColor: brandColor(),
          borderRadius: 4,
          barThickness: 'flex',
          maxBarThickness: 40,
        },
      ],
    };

    this.timelineChartOptions = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { display: false },
        tooltip: {
          backgroundColor: '#1e293b',
          padding: 12,
          cornerRadius: 8,
          callbacks: {
            label: (ctx) =>
              new Intl.NumberFormat(this.getLocale(), {
                style: 'currency',
                currency: 'EUR',
              }).format(ctx.parsed.y),
          },
        },
      },
      scales: {
        x: {
          grid: { display: false },
          ticks: { color: '#64748b', font: { size: 11 } },
        },
        y: {
          grid: { color: '#f1f5f9' },
          ticks: {
            color: '#64748b',
            font: { size: 11 },
            callback: (v) =>
              typeof v === 'number'
                ? v >= 1000
                  ? `${(v / 1000).toFixed(0)}k€`
                  : `${v}€`
                : v,
          },
          beginAtZero: true,
        },
      },
    };
  }

  private buildCategoryChart(): void {
    const colors = [brandColor(), '#8b5cf6', '#ec4899', '#f59e0b', '#10b981'];
    this.categoryChartData = {
      labels: this.revenueByCategory.map((c) => c.name),
      datasets: [
        {
          data: this.revenueByCategory.map((c) => c.value),
          backgroundColor: this.revenueByCategory.map(
            (c, i) => c.color || colors[i % colors.length],
          ),
          borderWidth: 0,
          borderRadius: 4,
          barThickness: 20,
        },
      ],
    };

    this.categoryChartOptions = {
      indexAxis: 'y',
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { display: false },
        tooltip: {
          backgroundColor: '#1e293b',
          padding: 12,
          cornerRadius: 8,
          callbacks: {
            label: (ctx) =>
              new Intl.NumberFormat(this.getLocale(), {
                style: 'currency',
                currency: 'EUR',
              }).format(ctx.parsed.x),
          },
        },
      },
      scales: {
        x: {
          grid: { display: false },
          ticks: {
            color: '#64748b',
            font: { size: 11 },
            callback: (v) =>
              typeof v === 'number'
                ? v >= 1000
                  ? `${(v / 1000).toFixed(0)}k€`
                  : `${v}€`
                : v,
          },
        },
        y: {
          grid: { display: false },
          ticks: { color: '#334155', font: { size: 11 } },
        },
      },
    } as ChartOptions;
  }

  private buildPaymentChart(): void {
    this.paymentChartData = {
      labels: this.revenueByPayment.map((p) =>
        this.translate.instant(`PAYMENT_TYPES.${p.name}`),
      ),
      datasets: [
        {
          data: this.revenueByPayment.map((p) => p.value),
          backgroundColor: this.revenueByPayment.map(
            (p) => p.color || brandColor(),
          ),
          borderWidth: 0,
          borderRadius: 4,
          barThickness: 24,
        },
      ],
    };

    this.paymentChartOptions = {
      indexAxis: 'y',
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { display: false },
        tooltip: {
          backgroundColor: '#1e293b',
          padding: 12,
          cornerRadius: 8,
          callbacks: {
            label: (ctx) =>
              new Intl.NumberFormat(this.getLocale(), {
                style: 'currency',
                currency: 'EUR',
              }).format(ctx.parsed.x),
          },
        },
      },
      scales: {
        x: {
          grid: { display: false },
          ticks: {
            color: '#64748b',
            font: { size: 11 },
            callback: (v) =>
              typeof v === 'number'
                ? v >= 1000
                  ? `${(v / 1000).toFixed(0)}k€`
                  : `${v}€`
                : v,
          },
        },
        y: {
          grid: { display: false },
          ticks: { color: '#334155', font: { size: 12, weight: 500 } },
        },
      },
    } as ChartOptions;
  }

  formatCurrency(value: number): string {
    return new Intl.NumberFormat(this.getLocale(), {
      style: 'currency',
      currency: 'EUR',
    }).format(value);
  }

  getTrendClass(trend: number | undefined): string {
    if (!trend || Math.abs(trend) < 0.01) return 'neutral';
    return trend > 0 ? 'positive' : 'negative';
  }

  getTrendIcon(trend: number | undefined): string {
    if (!trend || Math.abs(trend) < 0.01) return 'pi-minus';
    return trend > 0 ? 'pi-arrow-up' : 'pi-arrow-down';
  }

  getComparisonPeriodLabel(): string {
    if (!this.comparisonPeriod) return '';
    const start = new Date(this.comparisonPeriod.start);
    const end = new Date(this.comparisonPeriod.end);
    const options: Intl.DateTimeFormatOptions = {
      day: 'numeric',
      month: 'short',
    };
    const locale = this.getLocale();
    return `${start.toLocaleDateString(locale, options)} - ${end.toLocaleDateString(locale, options)}`;
  }

  private getLocale(): string {
    const lang = (this.translate.currentLang || 'es').toLowerCase();
    if (lang.startsWith('en')) return 'en-US';
    if (lang.startsWith('ca')) return 'ca-ES';
    return 'es-ES';
  }
}
